import React, { ReactElement, useState } from 'react'
import { useTranslate } from 'src/localization/Trns'

import SvgCopy from '../icons/SvgCopy'
import * as style from './CardRevealPanel.style'

type BillingAddress = {
  line1: string
  line2: string
  locality: string
  region: string
  postalCode: string
  countryCode: string
}

type CardRevealPanelProps = {
  /** Ties the panel to the Show details trigger via aria-controls. */
  id: string
  cardholderName: string
  address: BillingAddress
  pan: string
  cvc: string
}

// Resolves once the text is on the clipboard, rejects otherwise — a missing
// Clipboard API, a denied clipboard-write permission, or an unfocused document
// all reject. Callers must handle the rejection: a bare `void` would leak an
// unhandledrejection and still leave the user with no idea the copy failed.
const writeToClipboard = (text: string): Promise<void> => {
  if (!navigator.clipboard?.writeText) {
    return Promise.reject(new Error('Clipboard API unavailable'))
  }
  return Promise.resolve(navigator.clipboard.writeText(text))
}

const CopyButton = ({
  label,
  ariaLabel,
  value,
}: {
  label: string
  ariaLabel?: string
  value: string
}): ReactElement => {
  const translate = useTranslate()
  const [failed, setFailed] = useState(false)

  const onCopy = (): void => {
    setFailed(false)
    writeToClipboard(value).catch(() => setFailed(true))
  }

  return (
    <span css={style.copyGroup}>
      <button
        type="button"
        css={style.copyButton}
        aria-label={ariaLabel}
        onClick={onCopy}
      >
        <SvgCopy width={13} height={13} />
        {label}
      </button>
      {failed && (
        <span role="alert" css={style.copyError}>
          {translate("Couldn't copy — select and copy manually")}
        </span>
      )}
    </span>
  )
}

/**
 * The card-details reveal disclosure — billing identity, full card number, and
 * the plain visible CVC, each with a copy action. Not a modal: it is a labelled
 * region tied to the Show details trigger by id, so the page stays interactive.
 * Its open/close (Escape, outside click, timeout) is owned by the shell.
 */
const CardRevealPanel: React.FC<CardRevealPanelProps> = ({
  id,
  cardholderName,
  address,
  pan,
  cvc,
}): ReactElement => {
  const translate = useTranslate()

  const cityLine = `${address.locality}, ${address.region} ${address.postalCode}`
  const billingText = `${cardholderName}\n${address.line1}\n${address.line2}\n${cityLine}\n${address.countryCode}`

  return (
    <div id={id} css={style.panel} data-testid="card-reveal-panel">
      <div css={style.header}>
        <span css={style.headerLabel}>
          {translate('Billing name & address')}
        </span>
        <CopyButton
          label={translate('Copy')}
          ariaLabel={translate('Copy billing name and address')}
          value={billingText}
        />
      </div>
      <div css={style.body}>
        <div>{cardholderName}</div>
        <div>{address.line1}</div>
        <div>{address.line2}</div>
        <div>{cityLine}</div>
        <div>{address.countryCode}</div>
      </div>
      <div css={style.footer}>
        <CopyButton label={translate('Copy card number')} value={pan} />
        <span css={style.cvcGroup}>
          <CopyButton
            label={translate('CVC')}
            ariaLabel={translate('Copy CVC')}
            value={cvc}
          />
          <span css={style.cvcValue} data-testid="card-reveal-cvc">
            {cvc}
          </span>
        </span>
      </div>
    </div>
  )
}

export default CardRevealPanel
